<template>
  <div class="education-hub max-w-6xl mx-auto">
    <!-- 헤더 -->
    <div class="text-center mb-8">
      <div class="text-5xl mb-3">📚</div>
      <h1 class="text-4xl font-bold text-gradient mb-3">로또 가이드 센터</h1>
      <p class="text-gray-600 text-lg">재미있게 배우는 로또의 모든 것</p>
    </div>

    <!-- 빠른 링크 카드 -->
    <div class="grid md:grid-cols-3 gap-6 mb-12">
      <button
        v-for="category in categories"
        :key="category.id"
        @click="selectedCategory = category.id"
        :class="[
          'glass-card p-6 text-left transition-all duration-300 hover:scale-105',
          selectedCategory === category.id
            ? 'ring-2 ring-blue-500 shadow-xl'
            : 'hover:shadow-lg'
        ]"
      >
        <div class="text-4xl mb-3">{{ category.icon }}</div>
        <h3 class="text-xl font-bold mb-2">{{ category.name }}</h3>
        <p class="text-sm text-gray-600">{{ category.description }}</p>
      </button>
    </div>

    <!-- 가이드 목록 -->
    <div class="space-y-6">
      <template v-for="category in categories" :key="category.id">
        <section v-if="selectedCategory === 'all' || selectedCategory === category.id">
          <h2 class="text-2xl font-bold mb-4 flex items-center">
            <span class="text-3xl mr-3">{{ category.icon }}</span>
            {{ category.name }}
          </h2>
          
          <div class="grid md:grid-cols-2 gap-4">
            <div
              v-for="guide in category.guides"
              :key="guide.id"
              @click="$emit('navigate', guide.component)"
              class="glass-card p-5 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl group"
            >
              <div class="flex items-start">
                <div class="text-3xl mr-4 group-hover:scale-110 transition-transform">
                  {{ guide.icon }}
                </div>
                <div class="flex-1">
                  <h3 class="text-lg font-bold mb-2 group-hover:text-blue-600 transition-colors">
                    {{ guide.title }}
                  </h3>
                  <p class="text-sm text-gray-600 mb-3">{{ guide.description }}</p>
                  <div class="flex items-center text-xs text-gray-500">
                    <span class="mr-3">📖 {{ guide.readTime }}</span>
                    <span class="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      {{ guide.difficulty }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </template>
    </div>

    <!-- 추천 학습 경로 -->
    <section class="mt-12">
      <div class="glass-card p-8">
        <h2 class="text-2xl font-bold mb-6 flex items-center">
          <span class="text-3xl mr-3">🗺️</span>
          추천 학습 경로
        </h2>
        
        <div class="space-y-4">
          <!-- 초보자 경로 -->
          <div class="bg-green-50 p-5 rounded-xl border-l-4 border-green-400">
            <h3 class="font-bold text-lg mb-3 flex items-center">
              <span class="text-2xl mr-2">🌱</span>
              초보자를 위한 경로
            </h3>
            <ol class="space-y-2 text-sm text-gray-700 ml-6">
              <li>1️⃣ 로또 확률의 진실 이해하기</li>
              <li>2️⃣ 로또 번호 선택 방법 살펴보기</li>
              <li>3️⃣ AI 친구들 소개</li>
              <li>4️⃣ 책임있는 로또 구매 가이드</li>
            </ol>
          </div>

          <!-- 중급자 경로 -->
          <div class="bg-blue-50 p-5 rounded-xl border-l-4 border-blue-400">
            <h3 class="font-bold text-lg mb-3 flex items-center">
              <span class="text-2xl mr-2">📊</span>
              데이터 분석에 관심있는 분들을 위한 경로
            </h3>
            <ol class="space-y-2 text-sm text-gray-700 ml-6">
              <li>1️⃣ 로또 통계 분석 방법</li>
              <li>2️⃣ 통계의 오해와 진실</li>
              <li>3️⃣ 패턴 생성 원리 이해하기</li>
              <li>4️⃣ AI 모델의 작동 방식</li>
            </ol>
          </div>

          <!-- 고급 경로 -->
          <div class="bg-purple-50 p-5 rounded-xl border-l-4 border-purple-400">
            <h3 class="font-bold text-lg mb-3 flex items-center">
              <span class="text-2xl mr-2">🎓</span>
              심화 학습 경로
            </h3>
            <ol class="space-y-2 text-sm text-gray-700 ml-6">
              <li>1️⃣ 확률론의 기초</li>
              <li>2️⃣ 기댓값과 분산 이해하기</li>
              <li>3️⃣ 머신러닝 알고리즘 소개</li>
              <li>4️⃣ 수학적 패턴의 아름다움</li>
            </ol>
          </div>
        </div>
      </div>
    </section>

    <!-- 학습 팁 -->
    <section class="mt-8">
      <div class="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl border-2 border-yellow-300">
        <h3 class="font-bold text-lg mb-4 flex items-center">
          <span class="text-2xl mr-2">💡</span>
          효과적인 학습 팁
        </h3>
        <div class="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div class="flex items-start">
            <span class="text-xl mr-2">✅</span>
            <p><strong>편안한 마음으로:</strong> 로또는 재미를 위한 것입니다. 부담없이 배워보세요!</p>
          </div>
          <div class="flex items-start">
            <span class="text-xl mr-2">🔢</span>
            <p><strong>확률 이해가 핵심:</strong> 모든 조합이 동일한 확률을 가진다는 사실을 기억하세요.</p>
          </div>
          <div class="flex items-start">
            <span class="text-xl mr-2">🎨</span>
            <p><strong>창의성 발휘:</strong> 다양한 패턴과 AI를 시도해보며 재미를 찾아보세요.</p>
          </div>
          <div class="flex items-start">
            <span class="text-xl mr-2">📖</span>
            <p><strong>순서대로 학습:</strong> 기초부터 차근차근 배우면 더 재미있습니다.</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue';

// 이벤트 정의
const emit = defineEmits(['navigate']);

// 선택된 카테고리
const selectedCategory = ref('all');

// 카테고리 및 가이드 데이터
const categories = ref([
  {
    id: 'all',
    name: '전체 보기',
    icon: '📋',
    description: '모든 가이드를 한눈에',
    guides: []
  },
  {
    id: 'basics',
    name: '로또 기초',
    icon: '🎯',
    description: '로또의 기본 원리',
    guides: [
      {
        id: 'probability',
        title: '로또 확률의 진실',
        description: '모든 조합이 동일한 1/8,145,060 확률을 가진다는 사실',
        icon: '🎲',
        readTime: '5분',
        difficulty: '초급',
        component: 'ProbabilityGuide'
      },
      {
        id: 'how-to-play',
        title: '로또 번호 선택 방법',
        description: '수동 선택, 자동 선택, 반자동 선택의 차이',
        icon: '🎯',
        readTime: '4분',
        difficulty: '초급',
        component: 'HowToPlayGuide'
      },
      {
        id: 'responsible-gaming',
        title: '책임있는 로또 구매',
        description: '건강한 로또 참여를 위한 가이드',
        icon: '🛡️',
        readTime: '6분',
        difficulty: '초급',
        component: 'ResponsibleGamingGuide'
      },
      {
        id: 'history',
        title: '로또의 역사',
        description: '한국 로또 6/45의 시작과 발전',
        icon: '📜',
        readTime: '7분',
        difficulty: '초급',
        component: 'LottoHistoryGuide'
      }
    ]
  },
  {
    id: 'ai-characters',
    name: 'AI 친구들',
    icon: '🤖',
    description: '5명의 AI 캐릭터 소개',
    guides: [
      {
        id: 'luna',
        title: 'Luna - 패턴 탐험가',
        description: 'LSTM 모델로 시계열 패턴을 분석하는 호기심 많은 AI',
        icon: '🌙',
        readTime: '5분',
        difficulty: '중급',
        component: 'LunaGuide'
      },
      {
        id: 'pablo',
        title: 'Pablo - 창의적 예술가',
        description: 'GAN 모델로 새로운 조합을 만드는 혁신적인 AI',
        icon: '🎨',
        readTime: '5분',
        difficulty: '중급',
        component: 'PabloGuide'
      },
      {
        id: 'melody',
        title: 'Melody - 시퀀스 음악가',
        description: 'Transformer로 조화로운 번호를 만드는 리듬감있는 AI',
        icon: '🎵',
        readTime: '5분',
        difficulty: '중급',
        component: 'MelodyGuide'
      },
      {
        id: 'archie',
        title: 'Archie - 패턴 건축가',
        description: 'CNN 모델로 구조적 패턴을 찾는 논리적인 AI',
        icon: '🏛️',
        readTime: '5분',
        difficulty: '중급',
        component: 'ArchieGuide'
      },
      {
        id: 'morph',
        title: 'Morph - 꿈의 직조자',
        description: 'VAE 모델로 잠재 공간을 탐험하는 신비로운 AI',
        icon: '✨',
        readTime: '5분',
        difficulty: '중급',
        component: 'MorphGuide'
      }
    ]
  },
  {
    id: 'patterns',
    name: '패턴 이해',
    icon: '🎨',
    description: '다양한 패턴의 원리',
    guides: [
      {
        id: 'mathematical-patterns',
        title: '수학적 패턴',
        description: '피보나치, 소수, 완전제곱수 등 수학적 아름다움',
        icon: '🔢',
        readTime: '8분',
        difficulty: '중급',
        component: 'MathematicalPatternsGuide'
      },
      {
        id: 'visual-patterns',
        title: '시각적 패턴',
        description: '하트, 별, 십자가 등 도형 기반 패턴',
        icon: '⭐',
        readTime: '6분',
        difficulty: '초급',
        component: 'VisualPatternsGuide'
      },
      {
        id: 'statistical-patterns',
        title: '통계적 패턴',
        description: '핫넘버, 콜드넘버 등 출현 빈도 기반 패턴',
        icon: '📊',
        readTime: '7분',
        difficulty: '중급',
        component: 'StatisticalPatternsGuide'
      },
      {
        id: 'random-patterns',
        title: '랜덤 재미 패턴',
        description: '카오스 이론, 양자 붕괴 등 창의적 패턴',
        icon: '🎲',
        readTime: '6분',
        difficulty: '고급',
        component: 'RandomPatternsGuide'
      }
    ]
  },
  {
    id: 'advanced',
    name: '심화 학습',
    icon: '🎓',
    description: '깊이있는 이해',
    guides: [
      {
        id: 'statistics',
        title: '로또 통계 분석',
        description: '데이터 분석과 시각화 방법',
        icon: '📈',
        readTime: '10분',
        difficulty: '중급',
        component: 'StatisticsGuide'
      },
      {
        id: 'misconceptions',
        title: '통계의 오해와 진실',
        description: '도박사의 오류, 확률적 독립성 등',
        icon: '🚫',
        readTime: '8분',
        difficulty: '중급',
        component: 'MisconceptionsGuide'
      },
      {
        id: 'ml-basics',
        title: '머신러닝 기초',
        description: 'AI 모델들이 어떻게 작동하는지',
        icon: '🧠',
        readTime: '12분',
        difficulty: '고급',
        component: 'MLBasicsGuide'
      },
      {
        id: 'expected-value',
        title: '기댓값과 분산',
        description: '로또의 수학적 기댓값 계산',
        icon: '💰',
        readTime: '9분',
        difficulty: '고급',
        component: 'ExpectedValueGuide'
      }
    ]
  }
]);
</script>

<style scoped>
.text-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 1rem;
}
</style>
